import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LocationToggle from "@/components/LocationToggle";
import { formatLocation } from "@/utils/recoveryTerminology";

const RECOVERY_PROGRAMS = ["AA", "NA", "CA", "SMART Recovery", "Celebrate Recovery", "LifeRing", "Refuge Recovery", "Therapy Only", "Other"];

const ProfileEditPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    age: user?.age || "",
    gender: user?.gender || "",
    looking_for: user?.looking_for || "",
    location: formatLocation(user?.location) || "",
    photos: user?.photos || [],
    clean_date: user?.clean_date || user?.sobriety_date || "",
    recovery_program: user?.recovery_program || [],
    meeting_preferences: user?.meeting_preferences || [],
    min_age: user?.min_age || 18,
    max_age: user?.max_age || 99,
    max_distance: user?.max_distance || 50
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        age: user.age || "",
        gender: user.gender || "",
        looking_for: user.looking_for || "",
        location: formatLocation(user.location) || "",
        photos: user.photos || [],
        clean_date: user.clean_date || user.sobriety_date || "",
        recovery_program: user.recovery_program || [],
        meeting_preferences: user.meeting_preferences || [],
        min_age: user.min_age || 18,
        max_age: user.max_age || 99,
        max_distance: user.max_distance || 50
      });
    }
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (formData.photos.length >= 6) {
      toast.error("Maximum 6 photos allowed");
      return;
    }
    
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      try {
        const response = await api.uploadPhoto(base64);
        setFormData(prev => ({ ...prev, photos: response.data.photos }));
        updateUser({ photos: response.data.photos });
        toast.success("Photo uploaded!");
      } catch (error) {
        toast.error("Failed to upload photo");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = async (index) => {
    try {
      const response = await api.deletePhoto(index);
      setFormData(prev => ({ ...prev, photos: response.data.photos }));
      updateUser({ photos: response.data.photos });
      toast.success("Photo removed");
    } catch (error) {
      toast.error("Failed to remove photo");
    }
  };

  const toggleProgram = (program) => {
    setFormData(prev => ({
      ...prev,
      recovery_program: prev.recovery_program.includes(program)
        ? prev.recovery_program.filter(p => p !== program)
        : [...prev.recovery_program, program]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const profileData = {
        ...formData,
        age: parseInt(formData.age) || null,
        min_age: parseInt(formData.min_age) || 18,
        max_age: parseInt(formData.max_age) || 99,
        max_distance: parseInt(formData.max_distance) || 50
      };
      const response = await api.updateProfile(profileData);
      updateUser(response.data);
      toast.success("Profile updated!");
      navigate("/profile");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8" data-testid="profile-edit-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1C3A7A] to-[#0d1f3c] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate("/profile")} 
            className="p-2 hover:bg-white/10 rounded-full transition"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-lg font-bold text-white">Edit Profile</h1>
          <Button
            onClick={handleSave}
            disabled={loading}
            size="sm"
            className="bg-[#FAB710] hover:bg-[#e8a50f] text-white font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Photo Grid */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Photos</h2>
          <p className="text-xs text-gray-500 mb-3">Add up to 6 photos. Your first photo is your main profile picture.</p>
          <div className="grid grid-cols-3 gap-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="aspect-square relative">
                {formData.photos[index] ? (
                  <div className="relative w-full h-full">
                    <img
                      src={formData.photos[index]}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-red-600 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 bg-[#1C3A7A] text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                        Main
                      </span>
                    )}
                  </div>
                ) : (
                  <label className={`w-full h-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors ${uploadingPhoto ? 'border-gray-300 bg-gray-100' : 'border-gray-300 hover:border-[#FAB710] hover:bg-[#FAB710]/5'}`}>
                    {uploadingPhoto ? (
                      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-400 mt-1">Add</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Basic Info</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="h-12 rounded-xl"
                placeholder="Your name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Age</label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  className="h-12 rounded-xl"
                  placeholder="Age"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Gender</label>
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white text-gray-900"
                >
                  <option value="">Select</option>
                  <option value="Man">Man</option>
                  <option value="Woman">Woman</option>
                  <option value="Transgender Man">Transgender Man</option>
                  <option value="Transgender Woman">Transgender Woman</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Genderqueer">Genderqueer</option>
                  <option value="Genderfluid">Genderfluid</option>
                  <option value="Agender">Agender</option>
                  <option value="Two-Spirit">Two-Spirit</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Bio</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                rows={4}
                maxLength={500}
                className="rounded-xl resize-none"
                placeholder="Tell others about yourself..."
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.bio?.length || 0}/500</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Location</label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Philadelphia, PA"
                className="h-12 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* GPS Location Toggle */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Location Services</h2>
          <LocationToggle user={user} onUpdate={updateUser} />
        </div>

        {/* Recovery Programs */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Recovery Programs</h2>
          <p className="text-xs text-gray-500 mb-4">Select all that apply to you</p>
          <div className="flex flex-wrap gap-2">
            {RECOVERY_PROGRAMS.map(program => (
              <button
                key={program}
                type="button"
                onClick={() => toggleProgram(program)}
                className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                  formData.recovery_program.includes(program)
                    ? "border-[#FAB710] bg-[#FAB710]/10 text-[#FAB710]"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {program}
              </button>
            ))}
          </div>
        </div>

        {/* Match Preferences */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Match Preferences</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Looking For</label>
              <select
                value={formData.looking_for}
                onChange={(e) => setFormData(prev => ({ ...prev, looking_for: e.target.value }))}
                className="w-full h-12 px-3 border border-gray-200 rounded-xl bg-white text-gray-900"
              >
                <option value="">Select</option>
                <option value="Men">Men</option>
                <option value="Women">Women</option>
                <option value="Everyone">Everyone</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-3">Age Range: {formData.min_age} - {formData.max_age}</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Min Age</label>
                  <Input
                    type="number"
                    min="18"
                    max="99"
                    value={formData.min_age}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_age: e.target.value }))}
                    className="h-10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Max Age</label>
                  <Input
                    type="number"
                    min="18"
                    max="99"
                    value={formData.max_age}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_age: e.target.value }))}
                    className="h-10 rounded-xl"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Max Distance: {formData.max_distance} miles</label>
              <input
                type="range"
                min="5"
                max="100"
                value={formData.max_distance}
                onChange={(e) => setFormData(prev => ({ ...prev, max_distance: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FAB710]"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5 mi</span>
                <span>100 mi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full h-14 bg-[#FAB710] hover:bg-[#e8a50f] text-white rounded-xl font-bold text-base"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProfileEditPage;
